import React, { useEffect, useState } from 'react';
import { Alert, Box, LinearProgress, Paper, Typography, useTheme } from '@mui/material';
import { pushStatsService } from '../../services/pushStatsService';
import { IPushDeviceStats } from '../../types/pushStats';

const fmt = (n: number) => n.toLocaleString('en-GB');

const Headline: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 24, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {hint && (
      <Typography variant="caption" color="text.disabled">
        {hint}
      </Typography>
    )}
  </Box>
);

/** Snapshot of how many devices customers have registered; independent of the page's date range and filters. */
const DeviceStatsCard: React.FC = () => {
  const theme = useTheme();
  const [stats, setStats] = useState<IPushDeviceStats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    pushStatsService
      .getDeviceStats()
      .then((data) => !cancelled && setStats(data))
      .catch((e) => !cancelled && setError(typeof e === 'string' ? e : 'Failed to load device stats'));
    return () => {
      cancelled = true;
    };
  }, []);

  const mostCommon = stats
    ? stats.distribution.reduce((best, b) => (b.customers > best.customers ? b : best), stats.distribution[0])
    : null;
  const maxCustomers = stats ? Math.max(1, ...stats.distribution.map((b) => b.customers)) : 1;
  const barColor = theme.palette.mode === 'dark' ? '#64B5F6' : theme.palette.secondary.main;

  return (
    <Paper sx={{ p: 2, mb: 2 }} aria-labelledby="device-stats-title" component="section">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography id="device-stats-title" variant="subtitle1" sx={{ fontWeight: 500 }}>
          Devices per customer
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Current snapshot · not affected by the date range or filters
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {!stats && !error && <LinearProgress />}

      {stats && stats.customers === 0 && (
        <Typography color="text.secondary">No devices are registered yet.</Typography>
      )}

      {stats && stats.customers > 0 && (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1.4fr)' } }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', alignContent: 'start' }}>
            <Headline label="Customers with a device" value={fmt(stats.customers)} />
            <Headline label="Registered devices" value={fmt(stats.devices)} hint="distinct devices" />
            <Headline
              label="Most common"
              value={mostCommon ? `${mostCommon.label} device${mostCommon.label === '1' ? '' : 's'}` : '—'}
              hint={mostCommon ? `${((mostCommon.customers / stats.customers) * 100).toFixed(0)}% of customers` : undefined}
            />
            <Headline label="Average per customer" value={stats.averageDevices.toFixed(2)} hint={`most: ${stats.maxDevices}`} />
            <Headline
              label="Shared devices"
              value={fmt(stats.multiAccountDevices)}
              hint="registered under several accounts"
            />
          </Box>

          <Box role="table" aria-label="Customers by number of registered devices">
            {stats.distribution.map((b) => {
              const share = (b.customers / stats.customers) * 100;
              return (
                <Box
                  role="row"
                  key={b.label}
                  sx={{ display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr) 112px', alignItems: 'center', gap: 1.5, py: 0.5 }}
                >
                  <Typography role="cell" variant="body2" color="text.secondary">
                    {b.label} {b.label === '1' ? 'device' : 'devices'}
                  </Typography>
                  <Box role="cell" sx={{ height: 20, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${(b.customers / maxCustomers) * 100}%`, bgcolor: barColor, borderRadius: 1 }} />
                  </Box>
                  <Typography role="cell" variant="body2" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(b.customers)}{' '}
                    <Typography component="span" variant="caption" color="text.disabled">
                      {share.toFixed(1)}%
                    </Typography>
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default DeviceStatsCard;
